import { useEffect, useRef } from "react";
import type { RefObject } from "react";

interface UseInfiniteScrollArgs {
  /** Whether another page exists. No sentinel is observed when false. */
  hasMore: boolean;
  /** A fetch is already in flight; the sentinel stays observed but silent. */
  loading: boolean;
  onLoadMore: () => void;
}

/** Fires {@link UseInfiniteScrollArgs.onLoadMore} slightly before the sentinel
 *  scrolls into view, so the next page lands without the list stalling. */
const ROOT_MARGIN = "200px";

/**
 * Loads the next page when a sentinel element nears the viewport.
 *
 * Attach the returned ref to an element rendered *after* the list, and render
 * it only while `hasMore` is true: the observer is torn down and rebuilt
 * whenever `hasMore` flips, so an unmounted sentinel is never observed.
 */
export const useInfiniteScroll = ({
  hasMore,
  loading,
  onLoadMore,
}: UseInfiniteScrollArgs): RefObject<HTMLDivElement | null> => {
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasMore) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasMore && !loading) {
          onLoadMore();
        }
      },
      { rootMargin: ROOT_MARGIN },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, loading, onLoadMore]);

  return sentinelRef;
};
